import React from 'react'
import { useAuth } from '@/lib/AuthContext'
import { useCommunity } from '@/lib/CommunityContext'
import { useRoute } from '@/lib/RouteContext'
import { sdk } from '@/lib/sdk'
import type { MemberWelcome } from '@/types/welcome'
import { WelcomeFlow } from './WelcomeFlow'
import { setWelcomeActive } from './welcomeState'

/* The pages an e-mail link opens (set a password, an invite): the same exemption as "Complete seu cadastro". */
const EXEMPT_PREFIXES = ['/_/auth/', '/_/invite/']

/**
 * F-155: the member's first access goes through the community's welcome
 * before anything else, "Complete seu cadastro" included. `GET /auth/me` says
 * `welcomePending` for the community in `X-CommunityId`; the screens and where
 * the person stopped come from `GET /api/welcome`. Required, it covers the
 * whole app until the last screen (the API does not close the routes: the
 * block is this screen's, by the F-155 decision); skippable, "Pular" lets the
 * person in for good. The team is never pending.
 */
export const WelcomeGate = () => {
  const { user, activeMember, isRoleResolved, mustChangePassword, logout } = useAuth()
  const { community } = useCommunity()
  const { currentPath } = useRoute()
  const [welcome, setWelcome] = React.useState<MemberWelcome | null>(null)
  const memberId = activeMember?.id

  React.useEffect(() => {
    setWelcome(null)
    if (!user?.id || !isRoleResolved || !memberId) return
    let alive = true
    sdk.auth.me()
      .then(async (me) => {
        const answer = me as unknown as { welcomePending?: boolean; profiles?: { id: string; welcomePending?: boolean }[] }
        const pending = answer.welcomePending ?? answer.profiles?.find((p) => p.id === memberId)?.welcomePending ?? false
        if (!pending) return
        const mine = await sdk.welcome.mine()
        if (alive && mine.pending && mine.screens.length) setWelcome(mine)
      })
      .catch(() => { /* No answer, no welcome: the next visit asks again. */ })
    return () => { alive = false }
  }, [user?.id, isRoleResolved, memberId, community?.id])

  const exempt = EXEMPT_PREFIXES.some((prefix) => (currentPath || '').startsWith(prefix))
  const open = Boolean(welcome?.pending) && !mustChangePassword && !exempt
  React.useEffect(() => {
    setWelcomeActive(open)
  }, [open])
  React.useEffect(() => () => setWelcomeActive(false), [])

  if (!open || !welcome) return null
  /* After the last screen (or a skip) the API answers `pending: false`: the gate steps aside. */
  const apply = (next: MemberWelcome) => setWelcome(next.pending ? next : null)
  return (
    <WelcomeFlow
      screens={welcome.screens}
      step={welcome.step}
      mode={welcome.mode}
      onSubmit={async (answer) => apply(await sdk.welcome.progress({ step: welcome.step, ...answer }))}
      onSkip={welcome.mode === 'SKIPPABLE' ? async () => apply(await sdk.welcome.skip()) : undefined}
      onSignOut={logout}
    />
  )
}
